import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, tap } from 'rxjs';
import { environment } from '@env/environment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { GeneralService } from '@core/services/general.service';
import { DomainService } from '@core/services/domain.service';
import { MaintenanceHttpService } from '@core/services/maintenance-http.service';
import { Router } from '@angular/router';
import { PlatformService } from '@core/services/platform.service';
import { AuthHttpService } from '@core/entity/auth/service/auth.http.service';
import { Auth } from '@core/entity/auth/model/auth.model';
import { ApiResponse } from '@shared/models/api-response.model';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { diagTrackHttp } from '@core/diagnostics/ssr-diagnostics';
import { AuthTokenKey } from '@shared/local-storage.key';

export const apiInterceptor: HttpInterceptorFn = (req, next) => {

  // Absolute URLs (http://, https://) already point at their intended host
  // (e.g. the translation CDN). Skip the apiBase-prepending rewrite for them.
  if (req.url.startsWith('http://') || req.url.startsWith('https://')) {
    const diagAbsoluteDone = diagTrackHttp(`${req.method} ${req.urlWithParams}`);
    return next(req).pipe(finalize(() => diagAbsoluteDone()));
  }

  const
    deviceDetectorService = inject(DeviceDetectorService),
    generalService = inject(GeneralService),
    domainService = inject(DomainService),
    maintenanceService = inject(MaintenanceHttpService),
    platformService = inject(PlatformService),
    authHttpService = inject(AuthHttpService),
    eventEmitterService = inject(EventEmitterService),
    router = inject(Router),
    apiBase: string = environment.apiBase,
    isExternal: boolean = req.url.startsWith('//'),
    isIpAPI = req.url == '/getip',
    headersExcludeList = ['/getip', '/settingsystem/maintenance'];

  let
    request: HttpRequest<any> = setDomain(isExternal, isIpAPI, apiBase, req);

  /* Non External API */
  if (!isExternal ) {

    if(headersExcludeList.includes(request.url.replace(apiBase, ''))) {
      request = request.clone({
        headers: request.headers
          // .append('X-User-Agent', deviceDetectorService.userAgent)
          // .append('X-Language-Code', domainService.getLanguageCode())
          // .append('X-Country-Code', domainService.getCountryCode())
          // .append('X-Device-Type', generalService.getDeviceType())
          .append('X-Site-Prefix', environment.sitePrefix)
          // .append('X-Session-ID', generalService.getSessionId())
          // .append('X-Referrer', (window.location.href.includes('/gamelaunch?redirectDomain') ? window.location.href.split('?')[0] : window.location.href) + domainService.getMarketingParams())
          // .append('X-Is-Auth', authHttpService.getLoginStatus().toString())
      });
    } else {
      request = request.clone({
        headers: request.headers
          // .append('X-User-Agent', deviceDetectorService.userAgent)
          .append('X-Language-Code', domainService.getLanguageCode())
          .append('X-Country-Code', domainService.getCountryCode())
          .append('X-Device-Type', generalService.getDeviceType())
          .append('X-Site-Prefix', environment.sitePrefix)
          // .append('X-Session-ID', generalService.getSessionId())
          // .append('X-Referrer', (window.location.href.includes('/gamelaunch?redirectDomain') ? window.location.href.split('?')[0] : window.location.href) + domainService.getMarketingParams())
          // .append('X-Is-Auth', authHttpService.getLoginStatus().toString())
      });

      /* AIO-80915: device fingerprint header. api2 MemberAccount::traceInformation persists
         `fingerprint-id` into member_account_information.fingerprint_id — that is what /login
         and /register capture.
         Browser only: on the server the getter returns a 'server-*' placeholder, and a
         placeholder would make every account look like the same device. */
      if (platformService.isBrowser()) {
        const uuidFingerprint = generalService.getUuidFingerprint();
        if (uuidFingerprint) {
          request = request.clone({
            headers: request.headers
              .append('fingerprint-id', uuidFingerprint)
          });
        }
      }

      /* Member session headers. api2's Authenticate middleware reads exactly these two on
         every `member.*` route; without them it returns 403 "Please login to continue".
         That is why /login succeeded (Guest middleware) while every authenticated call —
         /profile included — came back 403.

         Two storage keys hold the same {access_token, plaintext_token} pair:
           - AuthTokenKey ('authToken') — written by the ngrx login effect, so this is the
             one a normal form login produces;
           - 'user_token' — written only by the cross-login / auto-login URL-param paths in
             app.component (socket.io-service and request-receipt read this one).
         Check both, AuthTokenKey first, so either way of arriving authenticated works.

         Read from localStorage rather than AuthHttpService.getAuthToken(): that returns the
         ngrx AuthState, which is empty on a fresh page load until the effect rehydrates, so
         header presence would otherwise depend on navigation order.
         Browser only — there is no member session to attach during SSR. */
      if (platformService.isBrowser()) {
        const readToken = (key: string): Auth | null => {
          try {
            return JSON.parse(localStorage.getItem(key) || '{}');
          } catch {
            return null;
          }
        };
        const sessionToken = [AuthTokenKey, 'user_token']
          .map(readToken)
          .find(token => !!(token?.access_token && token?.plaintext_token));

        if (sessionToken) {
          request = request.clone({
            headers: request.headers
              .append('access-token', sessionToken.access_token)
              .append('token-selector', sessionToken.plaintext_token)
          });
        }
      }
    }

    // if (!request.url.includes(environment.translationDomain) && !isIpAPI && !excludeList.includes(request.url.replace(apiBase, ''))) {
      
    //   /* Common */
    //   request = request.clone({
    //     headers: request.headers
    //       .append('X-User-Agent', deviceDetectorService.userAgent)
    //       .append('X-Language-Code', domainService.getLanguageCode())
    //       .append('X-Country-Code', domainService.getCountryCode())
    //       .append('X-Device-Type', generalService.getDeviceType())
    //       .append('X-Site-Prefix', environment.sitePrefix)
    //       .append('X-Session-ID', generalService.getSessionId())
    //       .append('X-Referrer', (window.location.href.includes('/gamelaunch?redirectDomain') ? window.location.href.split('?')[0] : window.location.href) + domainService.getMarketingParams())
    //       .append('fingerprint-id', generalService.getUuidFingerprint())
    //       .append('X-Is-Auth', authHttpService.getLoginStatus().toString())
    //   });
      
    //   /* Auth Token */
    //   if (authHttpService.getLoginStatus()) {
    //     let authToken: Auth = authHttpService.getAuthToken();

    //     request = request.clone({
    //       headers: request.headers
    //         .append('Authorization', `Bearer ${authToken.access_token}`)
    //         .append('access-token', `${authToken.access_token}`)
    //         .append('token-selector', `${authToken.plaintext_token}`)
    //     });

    //     /* Marketing Parameters */
    //     if (domainService.getMarketingParams() != '') {
    //       let urlParams = new URLSearchParams(domainService.getMarketingParams());

    //       if (urlParams.has('affid')) {
    //         request = request.clone({
    //           headers: request.headers
    //             .append('affid', urlParams.get('affid'))
    //         });
    //       }

    //       if (urlParams.has('clickid')) {
    //         request = request.clone({
    //           headers: request.headers
    //             .append('clickid', urlParams.get('clickid'))
    //         });
    //       }

    //       if (urlParams.has('retargeting')) {
    //         request = request.clone({
    //           headers: request.headers
    //             .append('retargeting', urlParams.get('retargeting'))
    //         });
    //       }

    //       if (urlParams.has('telemarketer_id')) {
    //         request = request.clone({
    //           headers: request.headers
    //             .append('telemarketer-id', urlParams.get('telemarketer_id'))
    //         });
    //       }
    //     }

    //     // if (localStorage.getItem('os-player-id') === null) {
    //     //   window.OneSignal?.push(function () {
    //     //     window.OneSignal.isPushNotificationsEnabled(function (isEnabled) {
    //     //       if (isEnabled) {
    //     //         window.OneSignal.getUserId(function (userId) {
    //     //           localStorage.setItem('os-player-id', userId);
    //     //         });
    //     //       }
    //     //     });
    //     //   });
    //     // }

    //     // // if (localStorage.getItem('os-player-id')) {
    //     // //   request = request.clone({
    //     // //     headers: request.headers
    //     // //       .append('OS-Player-ID', localStorage.getItem('os-player-id'))
    //     // //   }
    //     // //   );
    //     // // }
    //   }

    // } else {
    //   request = request.clone({
    //     headers: request.headers
    //       // .append('X-User-Agent', deviceDetectorService.userAgent)
    //       // .append('X-Language-Code', domainService.getLanguageCode())
    //       // .append('X-Country-Code', domainService.getCountryCode())
    //       // .append('X-Device-Type', generalService.getDeviceType())
    //       .append('X-Site-Prefix', environment.sitePrefix)
    //       // .append('X-Session-ID', generalService.getSessionId())
    //       // .append('X-Referrer', (window.location.href.includes('/gamelaunch?redirectDomain') ? window.location.href.split('?')[0] : window.location.href) + domainService.getMarketingParams())
    //       // .append('fingerprint-id', generalService.getUuidFingerprint())
    //       // .append('X-Is-Auth', authHttpService.getLoginStatus().toString())
    //   });
    // }
  }
  /* External API  */
  else {

  }
  if (platformService.isBrowser()) {
  } else {
  }

  const diagHttpDone = diagTrackHttp(`${request.method} ${request.urlWithParams}`);

  return next(request).pipe(
    /* Continue here if API Success */
    tap(res => {
      /* Check API Version */
      if (platformService.isBrowser() && res instanceof HttpResponse) {
        const
          currentApiVersion = localStorage.getItem('api_version'),
          apiResponse: ApiResponse = res.body as ApiResponse;

        if (apiResponse.version) {
          eventEmitterService.updateApiVersion.emit(apiResponse.version);
          if (currentApiVersion == null) {
            localStorage.setItem('api_version', apiResponse.version);
          } else {
            if (currentApiVersion != apiResponse.version) {
              localStorage.setItem('api_version', apiResponse.version);
              if (authHttpService.getLoginStatus()) {
                authHttpService.logout();
              }
              window.location.reload();
            }
          }
        }
      }
    }),
    /* Continue here if API Error */
    catchError((err: HttpErrorResponse) => {
      /* Scheduled Maintenance */
      if (err.status == 503 && err?.error?.message[0] == 'The system is unavailable due to scheduled maintenance.') {
        maintenanceService.setUnderMaintenance(true);
        maintenanceService.setStartDateTime(err.error.data.rows.start_maintenance_time);
        maintenanceService.setEndDateTime(err.error.data.rows.end_maintenance_time);
        maintenanceService.setRemainingTime(err.error.data.rows.remaining_time);
        router.navigate(['maintenance']);
      }
      throw err;
    }),
    /* Last in the pipe so it fires on complete, error and unsubscribe alike. */
    finalize(() => diagHttpDone())
  );
};

function setDomain(isExternal: boolean, isIpAPI: boolean, apiBase: string, request: HttpRequest<any>): HttpRequest<any> {
  request = request.clone({
    url: isExternal ? 'https:' + request.url : (isIpAPI ? apiBase.substring(0, apiBase.lastIndexOf('/')) : apiBase) + request.url
  });

  return request;
}